/**
 * Export a list to CSV, Excel or PDF.
 *
 * jsPDF and SheetJS are loaded on demand: together they weigh more than a
 * megabyte, and most sessions never export anything. The Turkish font is
 * fetched the same way — jsPDF's built-in faces are Latin-1, which has no
 * ş/ğ/ı, so without it every Turkish name comes out mangled.
 */

export type ExportFormat = "csv" | "xlsx" | "pdf";

export interface ExportColumn<T> {
  header: string;
  /** Cell value; return "" for blanks rather than null so every format agrees. */
  value: (row: T) => string | number;
}

export interface ExportRequest<T> {
  rows: T[];
  columns: ExportColumn<T>[];
  /** File name without extension. */
  filename: string;
  /** Shown as the PDF's heading. */
  title: string;
  /** "Filter: value" lines recorded under the title, so a saved file says what it was filtered by. */
  filters?: Array<{ label: string; value: string }>;
  /**
   * Who exported it, when, and in which time zone — written under the title so
   * a saved list says when it was taken. Lists get mixed up otherwise.
   */
  meta?: Array<{ label: string; value: string }>;
}

const FONT_URL = "/fonts/DejaVuSans-tr.ttf";
const FONT_BOLD_URL = "/fonts/DejaVuSans-Bold-tr.ttf";

const cell = <T,>(col: ExportColumn<T>, row: T): string => {
  const v = col.value(row);
  return v === null || v === undefined ? "" : String(v);
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Excel splits on the list separator of the machine's locale, which is ";" on
 * Turkish Windows, and it only reads the file as UTF-8 when it starts with a
 * BOM. Without both, an exported list opens as one mashed column of mojibake.
 */
const toCsv = <T,>({ rows, columns, filters, title, meta }: ExportRequest<T>): Blob => {
  const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const lines: string[] = [escape(title)];

  for (const m of meta ?? []) lines.push(`${escape(m.label)};${escape(m.value)}`);

  for (const f of filters ?? []) lines.push(`${escape(f.label)};${escape(f.value)}`);
  if (filters?.length) lines.push("");

  lines.push(columns.map((c) => escape(c.header)).join(";"));
  for (const row of rows) {
    lines.push(columns.map((c) => escape(cell(c, row))).join(";"));
  }

  return new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
};

const toXlsx = async <T,>({ rows, columns, filename, filters, title, meta }: ExportRequest<T>) => {
  const XLSX = await import("xlsx");

  const head: any[][] = [[title]];
  for (const m of meta ?? []) head.push([m.label, m.value]);
  for (const f of filters ?? []) head.push([f.label, f.value]);
  if (filters?.length) head.push([]);
  head.push(columns.map((c) => c.header));

  const body = rows.map((row) => columns.map((c) => c.value(row) ?? ""));
  const sheet = XLSX.utils.aoa_to_sheet([...head, ...body]);

  // Widen columns to their contents so nothing opens as "#####".
  sheet["!cols"] = columns.map((c) => ({
    wch: Math.min(
      60,
      Math.max(c.header.length + 2, ...rows.map((r) => cell(c, r).length + 2), 10)
    ),
  }));

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Liste");
  XLSX.writeFile(book, `${filename}.xlsx`);
};

const loadFont = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`font yüklenemedi: ${url}`);
  const buf = await res.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  // btoa on a 100KB+ string via spread blows the call stack, hence the chunking.
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + 8192))
    );
  }
  return btoa(binary);
};

/** Builds the document but does not save it, so it can be rendered in a test. */
export const buildPdf = async <T,>({ rows, columns, title, filters, meta }: ExportRequest<T>) => {
  const [{ jsPDF }, autoTableModule, regular, bold] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
    loadFont(FONT_URL),
    loadFont(FONT_BOLD_URL),
  ]);
  const autoTable = (autoTableModule as any).default ?? autoTableModule;

  // Landscape: these lists are wider than they are long.
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  doc.addFileToVFS("DejaVuSans.ttf", regular);
  doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
  doc.addFileToVFS("DejaVuSans-Bold.ttf", bold);
  doc.addFont("DejaVuSans-Bold.ttf", "DejaVu", "bold");
  doc.setFont("DejaVu", "normal");

  let y = 40;
  doc.setFont("DejaVu", "bold").setFontSize(14);
  doc.text(title, 40, y);
  y += 18;

  doc.setFont("DejaVu", "normal").setFontSize(9).setTextColor(110);
  doc.text(`${rows.length} kayıt`, 40, y);
  y += 14;
  for (const m of meta ?? []) {
    doc.text(`${m.label}: ${m.value}`, 40, y);
    y += 12;
  }
  for (const f of filters ?? []) {
    doc.text(`${f.label}: ${f.value}`, 40, y);
    y += 12;
  }
  doc.setTextColor(0);

  autoTable(doc, {
    startY: y + 6,
    head: [columns.map((c) => c.header)],
    body: rows.map((row) => columns.map((c) => cell(c, row))),
    styles: { font: "DejaVu", fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { font: "DejaVu", fontStyle: "bold", fillColor: [243, 244, 246], textColor: 20 },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    margin: { left: 40, right: 40 },
  });

  return doc;
};

const toPdf = async <T,>(request: ExportRequest<T>) => {
  const doc = await buildPdf(request);
  doc.save(`${request.filename}.pdf`);
};

export const exportList = async <T,>(
  format: ExportFormat,
  request: ExportRequest<T>
): Promise<void> => {
  if (format === "csv") {
    triggerDownload(toCsv(request), `${request.filename}.csv`);
    return;
  }
  if (format === "xlsx") return toXlsx(request);
  return toPdf(request);
};

export interface PagedResponse<T> {
  items?: T[];
  pagination?: { totalPages?: number; totalItems?: number; total?: number };
  totalPages?: number;
}

/**
 * Walk every page of a list rather than asking for one enormous one.
 *
 * A single `pageSize=10000` request works only for as long as no one puts a
 * ceiling on page size; the day someone does, exports quietly start returning
 * the first N rows and the file looks complete. Paging is immune to that, and
 * `maxRows` stops a runaway list from being pulled into the browser whole.
 */
export const fetchAllPages = async <T,>(
  fetchPage: (page: number, pageSize: number) => Promise<PagedResponse<T>>,
  { pageSize = 500, maxRows = 50000 }: { pageSize?: number; maxRows?: number } = {}
): Promise<T[]> => {
  const all: T[] = [];
  let page = 1;
  // What a full page actually looks like, learned from the first response.
  // Comparing against the size we *asked* for would end the walk on page one
  // wherever the server caps page size — the very case paging exists for.
  let fullPage = 0;

  while (all.length < maxRows) {
    const res = await fetchPage(page, pageSize);
    const items = res?.items ?? [];
    all.push(...items);

    if (items.length === 0) break;
    if (page === 1) fullPage = items.length;

    const totalItems = res?.pagination?.totalItems ?? res?.pagination?.total;
    if (typeof totalItems === "number" && all.length >= totalItems) break;

    const totalPages = res?.pagination?.totalPages ?? res?.totalPages;
    if (typeof totalPages === "number" && page >= totalPages) break;

    // Short page means the last one.
    if (items.length < fullPage) break;
    page += 1;
  }

  return all.slice(0, maxRows);
};
